<script lang="ts">
  // The Zwift and Wahoo Sync pages (/sync/{type}, UserController@getThirdPartyLoginPage): connect or
  // disconnect the app, choose whether scheduled workouts are sent to it (and, for Zwift, whether
  // its activities sync and which types), and see the workouts Xert has sent.
  import { Button } from '$lib/components/ui/button'
  import * as Card from '$lib/components/ui/card'
  import SettingSwitch from '@/Common/SettingSwitch.svelte'
  import ActivityTypes from '@/Integration/Sync/ActivityTypes.svelte'
  import ActivityTypesDialog from '@/Integration/Sync/ActivityTypesDialog.svelte'
  import ConnectButton from '@/Integration/Sync/ConnectButton.svelte'
  import SyncConnection from '@/Integration/Sync/SyncConnection.svelte'
  import SyncHelp from '@/Integration/Sync/SyncHelp.svelte'
  import SyncHelpItem from '@/Integration/Sync/SyncHelpItem.svelte'
  import { SERVICES } from '@/Integration/Sync/services'
  import { removeWorkoutPlayerWorkout, saveWorkoutPlayerSyncSettings, type BasicResponse } from '@/Util/api'
  import { ErrorCode } from '@/Util/errorCodes'
  import { notifyApiError } from '@/Util/notify'

  type Sent = { [key: string]: any }

  export let service: 'zwift' | 'wahoo'
  export let connected: boolean
  export let isPremium: boolean
  /** Zwift's activities can sync to Xert; Wahoo only takes workouts. */
  export let canSyncActivities = false
  export let sport = 'Cycling'
  export let sendWorkouts = false
  export let syncActivities = false
  export let types: string[] = []
  /** What Xert has sent to the app, or null when the app couldn't be reached. */
  export let workouts: { workouts: Sent | Sent[], scheduled: Sent | Sent[] } | null = null
  export let connectUrl: string
  export let disconnectUrl: string
  export let settingsUrl: string
  export let upgradeUrl: string

  $: name = SERVICES[service].name

  const SPORTS = ['Cycling', 'Running', 'Other']
  let typeOptions = SPORTS.map((value) => ({ value, checked: types.includes(value) }))
  $: chosenTypes = typeOptions.filter((t) => t.checked).map((t) => t.value)
  let typesOpen = false

  function saved(res: BasicResponse, message: string) {
    if (res.success) return true
    notifyApiError(res, { code: ErrorCode.SYNC_SETTING_FAILED, message })
    return false
  }

  const saveSendWorkouts = async (on: boolean) =>
    saved(await saveWorkoutPlayerSyncSettings(settingsUrl, { send_workouts: on }), 'Could not save your workout setting. Please try again.')

  // Syncing with no types picked starts with the athlete's own sport.
  async function saveSyncActivities(on: boolean) {
    const start = on && !chosenTypes.length ? [SPORTS.includes(sport) ? sport : 'Other'] : null
    const ok = saved(await saveWorkoutPlayerSyncSettings(settingsUrl, { sync_activities: on, ...(start ? { sync_activity_types: start } : {}) }),
      'Could not save your activity sync setting. Please try again.')
    if (ok && start) typeOptions = typeOptions.map((t) => ({ ...t, checked: start.includes(t.value) }))
    return ok
  }

  const saveTypes = async (types: string[]) =>
    saved(await saveWorkoutPlayerSyncSettings(settingsUrl, { sync_activity_types: types }), 'Could not save your activity types. Please try again.')

  // The two lists come keyed by id from Zwift and as arrays from Wahoo.
  type Row = { key: string, name: string, date?: Date, removeUrl?: string, completed: boolean }
  const toRows = (items: Sent | undefined, dateOf: (v: Sent) => string | undefined): Row[] =>
    Object.entries(items ?? {}).map(([key, v]) => {
      const date = dateOf(v)
      return {
        key,
        name: v.name ?? v.workoutSummary?.name ?? (v.error ? 'Unavailable' : 'Workout'),
        date: date ? new Date(date) : undefined,
        removeUrl: v.delete,
        completed: v.workoutState === 'COMPLETED',
      }
    })
  // Keyed once, so a removal doesn't renumber Wahoo's arrays under another removal still on its way.
  const keyed = (items: Sent | Sent[] | undefined): Sent => Object.fromEntries(Object.entries(items ?? {}))
  let sent = workouts && { workouts: keyed(workouts.workouts), scheduled: keyed(workouts.scheduled) }
  $: scheduled = toRows(sent?.scheduled, (v) => v.starts ?? v.startTime)
  $: library = toRows(sent?.workouts, (v) => v.created_at)

  const dateFormat = new Intl.DateTimeFormat(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' })

  let removing = new Set<string>()
  async function remove(list: 'scheduled' | 'workouts', row: Row) {
    if (!row.removeUrl || !sent) return
    removing = new Set(removing).add(row.key)
    const res = await removeWorkoutPlayerWorkout(row.removeUrl)
    removing.delete(row.key)
    removing = removing
    if (!res.success) {
      notifyApiError(res, { code: ErrorCode.SYNC_WORKOUT_REMOVE_FAILED, message: 'Could not remove that workout. Please try again.' })
      return
    }
    const { [row.key]: _, ...rest } = sent[list]
    sent[list] = rest
  }
</script>

<div class="mx-auto flex max-w-[1100px] flex-col gap-4 px-4 pb-8">
  <SyncConnection {service} {connected} {disconnectUrl}
    benefits={[
      `Send your scheduled Xert workouts to ${name} automatically`,
      ...(canSyncActivities ? [`Import the activities you record on ${name} into Xert`] : []),
      `Send any Xert workout to ${name} when you want to ride it`,
    ]}
    disconnectMessage={`Xert stops sending workouts to ${name}${canSyncActivities ? ` and importing your ${name} activities` : ''}. Activities already in Xert stay.`}>
    <svelte:fragment slot="connect">
      <ConnectButton {service} href={connectUrl} disabled={!isPremium} />
      {#if !isPremium}
        <span class="text-xs text-muted-foreground">Needs a Premium subscription. <a href={upgradeUrl} class="font-medium text-primary underline-offset-2 hover:underline">Upgrade</a></span>
      {/if}
    </svelte:fragment>
  </SyncConnection>

  {#if connected}
    <div class="grid gap-4 lg:grid-cols-2">
      <Card.Root>
        <Card.Header class="pb-0">
          <Card.Title class="text-base">Automatic sync</Card.Title>
          <Card.Description>Workouts are sent as they're scheduled{canSyncActivities ? ', and activities sync as soon as they\'re recorded' : ''}.</Card.Description>
        </Card.Header>
        <Card.Content class="divide-y divide-border">
          <SettingSwitch id="{service}-send-workouts" bind:checked={sendWorkouts} save={saveSendWorkouts}
            label="Send scheduled workouts to {name}"
            description="Upcoming workouts in your Xert calendar are sent to {name}." />
          {#if canSyncActivities}
            <SettingSwitch id="{service}-sync-activities" bind:checked={syncActivities} save={saveSyncActivities}
              label="Import new {name} activities into Xert"
              description="Activities of the types below are added to Xert when you finish them on {name}." />
            {#if syncActivities}
              <ActivityTypes {chosenTypes} on:click={() => (typesOpen = true)} />
            {/if}
          {/if}
        </Card.Content>
      </Card.Root>

      <Card.Root>
        <Card.Header class="pb-0">
          <Card.Title class="text-base">Workouts on {name}</Card.Title>
          <Card.Description>What Xert has sent to {name}.</Card.Description>
        </Card.Header>
        <Card.Content class="flex flex-col gap-4 pt-3">
          {#if !sent}
            <p class="m-0 text-sm text-muted-foreground">Couldn't load your workouts from {name}. Please refresh the page to try again.</p>
          {:else if !scheduled.length && !library.length}
            <p class="m-0 text-sm text-muted-foreground">
              Nothing sent yet. {sendWorkouts ? `Your next scheduled workout is sent to ${name} automatically.` : `Turn on Send scheduled workouts, or send a workout from My Fitness.`}
            </p>
          {:else}
            {#each [{ id: 'scheduled', title: 'Scheduled', rows: scheduled, action: 'Unschedule' }, { id: 'workouts', title: 'In your library', rows: library, action: 'Remove' }] as section (section.id)}
              {#if section.rows.length}
                <div class="flex flex-col gap-1">
                  <span class="text-xs font-medium uppercase tracking-wide text-muted-foreground">{section.title}</span>
                  <ul class="m-0 flex list-none flex-col divide-y divide-border p-0">
                    {#each section.rows as row (row.key)}
                      <li class="flex items-center justify-between gap-3 py-2">
                        <div class="flex min-w-0 flex-col">
                          <span class="truncate text-sm font-medium text-foreground">{row.name}</span>
                          {#if row.date}<span class="text-xs text-muted-foreground">{dateFormat.format(row.date)}</span>{/if}
                        </div>
                        {#if row.completed}
                          <span class="shrink-0 rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-800">Completed</span>
                        {:else if row.removeUrl}
                          <Button variant="outline" size="sm" class="shrink-0" disabled={removing.has(row.key)}
                            on:click={() => remove(section.id === 'scheduled' ? 'scheduled' : 'workouts', row)}>
                            {#if removing.has(row.key)}<i class="fa-solid fa-spinner-third fa-spin mr-2" aria-hidden="true" />{/if}{section.action}
                          </Button>
                        {/if}
                      </li>
                    {/each}
                  </ul>
                </div>
              {/if}
            {/each}
          {/if}
        </Card.Content>
      </Card.Root>
    </div>
  {/if}

  <SyncHelp>
    <SyncHelpItem title="Syncing problems?">Disconnect {name}, connect again and allow every permission {name} asks for.</SyncHelpItem>
    <SyncHelpItem title="Riding a different workout?">Open it on the <a href="/my-fitness">My Fitness page</a> and send it to {name} from its actions.</SyncHelpItem>
    <SyncHelpItem title="Need a hand setting up?">Follow the <a href={SERVICES[service].guide} target="_blank" rel="noopener">{name} setup guide</a>.</SyncHelpItem>
  </SyncHelp>
</div>

{#if connected && canSyncActivities}
  <ActivityTypesDialog id={service} serviceName={name} bind:open={typesOpen} bind:typeOptions save={saveTypes} />
{/if}
